import { Command, Option, type AddHelpTextPosition, type Argument, type OutputConfiguration } from 'commander';
import type { CommandActionModule } from '../types/CommandAction.js';
import type { SharedOptions } from '../types/SharedOptions.js';
import { CommandExecutor, type CommandExecutorParams } from './CommandExecutor.js';
import { environmentInfo } from '@ms-cloudpack/environment';
import { parseFeature } from './parseFeature.js';
import { parseDefine } from './parseDefine.js';
import { CloudpackHelp, optionGroups } from './CloudpackHelp.js';

/**
 * Parameters for creating a command.
 * `TOptions` should be the command-specific options (if any), e.g. `StartOptions`.
 */
export interface CloudpackCommandParams<TOptions extends object, TArguments extends object> extends Partial<
  Pick<CommandExecutorParams<TOptions>, 'discoverAppPaths' | 'enableReporterReset' | 'applyOptionsToConfig'>
> {
  /** Command name */
  name: string;

  /** Command description */
  description: string;

  /**
   * Options specific to this command. The record ensures that all options are available in the CLI.
   * Allow multiple options for the same key since commander supports it with negatable options.
   */
  options?: Record<keyof TOptions, Option | Option[]>; //

  /** Arguments specific to this command. Key order determines the arg order. */
  args?: Record<keyof TArguments, Argument>;

  /**
   * Function returning an async import of the command's `src/commands/<name>/execute.ts` module.
   * Importing only on demand makes Cloudpack start up faster since it doesn't have to parse
   * every command's dependencies (many of which may be unrelated to the command being run).
   *
   * This can be undefined for a command that only has sub-commands.
   */
  getExecutor?: () => Promise<CommandActionModule<TOptions>>;

  /** Usage message override */
  usage?: string;

  /** Alternative name for the command */
  alias?: string;

  /** Extra text to be displayed with the built-in help. */
  addHelpText?: {
    /**
     * `'before'` or `'after'` to affect just this command.
     * `'beforeAll'` or `'afterAll'` to affect this command and all its subcommands.
     */
    position: AddHelpTextPosition;
    text: string;
  };

  /** Output options override (mainly for testing) */
  outputOptions?: OutputConfiguration;

  /**
   * The shared options are relevant for most commands, but this allows omitting them from help
   * where they're not relevant. `true` hides all shared options, or an array hides specific ones.
   */
  hideSharedOptions?: Array<keyof SharedOptions> | true;
}

/**
 * This wraps the `Command` class from [`commander`](https://www.npmjs.com/package/commander) with
 * Cloudpack-specific functionality and simpler APIs.
 *
 * Similar to the pattern from `commander`, this class represents both the program and its sub-commands.
 * - The top-level "program" command should be created with `new CloudpackCommand()`.
 * - Sub-commands should be created with the `.addSubCommand` method on the parent.
 */
export class CloudpackCommand<TOptions extends object, TArguments extends object> {
  private _command: Command;
  private _executorOptions: Pick<
    CommandExecutorParams<TOptions>,
    'programOptions' | 'discoverAppPaths' | 'enableReporterReset' | 'applyOptionsToConfig'
  >;

  /**
   * This should only be used directly (outside this class) to create the top-level `program` command.
   * To add sub-commands, use `.addSubCommand` instead.
   */
  constructor(
    // The extra options here shouldn't be exposed to the `createSubCommand` API
    params: CloudpackCommandParams<TOptions, TArguments> &
      Pick<CommandExecutorParams<TOptions>, 'programOptions'> & {
        /** This must be undefined for the top-level program command */
        parent?: Command;
      },
  ) {
    this._executorOptions = {
      programOptions: params.programOptions,
      discoverAppPaths: params.discoverAppPaths,
      enableReporterReset: params.enableReporterReset,
      applyOptionsToConfig: params.applyOptionsToConfig,
    };

    const command = params.parent?.command(params.name) || new Command(params.name);
    this._command = command;

    command.version(params.programOptions.version);
    command.description(params.description);
    command.allowExcessArguments(false);

    const isTopLevel = !params.parent;

    if (isTopLevel && environmentInfo.isJest) {
      // In tests, throw instead of exiting the process on invalid args
      command.exitOverride((err) => {
        throw new Error(`Process exited with code ${err.exitCode} due to arg parsing error`);
      });
    }

    command.createHelp = () => new CloudpackHelp({ isTopLevel });
    // set the default help group for options
    command.optionsGroup(optionGroups.default);

    params.usage && command.usage(params.usage);
    params.alias && command.alias(params.alias);
    params.addHelpText && command.addHelpText(params.addHelpText.position, params.addHelpText.text);
    params.outputOptions && command.configureOutput(params.outputOptions);
    params.getExecutor && this._addAction(params.getExecutor);

    if (params.args) {
      this._addArguments<TArguments>(params.args);
    }

    if (params.options) {
      // Add the command-specific options first so they show up first in help
      this._addOptions<TOptions>(params.options);
    }

    // Add shared options. These must be manually added to the program command and each sub-command
    // because commander doesn't natively support inheriting options.
    this._addOptions<SharedOptions>(
      {
        verbose: new Option('-v, --verbose', 'Show additional details in logging.').helpGroup(optionGroups.logging),
        debug: new Option('-d, --debug', 'Show debug information (superset of --verbose).')
          .conflicts('verbose')
          .helpGroup(optionGroups.logging),
        quiet: new Option('-q, --quiet', 'Disable non-essential logging.')
          .conflicts('verbose')
          .conflicts('debug')
          .helpGroup(optionGroups.logging),
        color:
          // In the parsed options, this will be a boolean `color` with default value true, except in tests, CI, or environments without a TTY (e.g., concurrently).
          new Option('-n, --no-color', 'Disable colors in the output.')
            .helpGroup(optionGroups.logging)
            .default(
              !environmentInfo.isJest && !environmentInfo.isLage && !environmentInfo.isCI && !!process.stdout.isTTY,
            ),
        logFile: new Option('--log-file', 'Write a log file with all console output.').helpGroup(optionGroups.logging),
        cachePath: new Option(
          '--cache-path <path>',
          'Custom cache folder. To change the default, run `cloudpack config set cachePath <path>`.',
        ).helpGroup(optionGroups.common),
        config: new Option(
          '-c, --config <filename>',
          'Custom config file name to use instead of cloudpack.config.json.',
        ).helpGroup(optionGroups.common),
        define: new Option('--define <key=value>', 'Define a global variable. Can be specified multiple times.')
          .helpGroup(optionGroups.common)
          .argParser(parseDefine),
        features: new Option('--features <featureNames...>', 'Enable experimental features.')
          .helpGroup(optionGroups.common)
          .argParser(parseFeature),
      },
      params.hideSharedOptions,
    );
  }

  /** Create a child command. */
  public addSubCommand<TChildOptions extends object, TChildArguments extends object = never>(
    params: CloudpackCommandParams<TChildOptions, TChildArguments>,
  ): CloudpackCommand<TChildOptions, TChildArguments> {
    return new CloudpackCommand<TChildOptions, TChildArguments>({
      ...params,
      programOptions: this._executorOptions.programOptions,
      parent: this._command,
    });
  }

  /** Run the program (only works on the top-level program command). */
  public async run(): Promise<void> {
    if (this._command.parent) {
      throw new Error('Only the top-level program command can be run directly.');
    }

    await this._command.parseAsync(this._executorOptions.programOptions.argv);
    if (this._command.args.length === 0) {
      this._command.help();
    }
  }

  /** Add arguments. The generic enforces that argument objects are included for all keys of a type. */
  private _addArguments<TAddArguments extends object>(args: Record<keyof TAddArguments, Argument>): void {
    for (const argument of Object.values(args)) {
      this._command.addArgument(argument as Argument);
    }
  }

  /** Add options. The generic enforces that option objects are included for all keys of a type. */
  private _addOptions<TAddOptions extends object>(
    options: Record<keyof TAddOptions, Option | Option[]>,
    hideOptions?: Array<keyof TAddOptions> | true,
  ): void {
    for (const [name, optionOrOptions] of Object.entries(options) as [keyof TAddOptions, Option | Option[]][]) {
      const optionArray = Array.isArray(optionOrOptions) ? optionOrOptions : [optionOrOptions];
      for (const option of optionArray) {
        if (hideOptions === true || hideOptions?.includes(name)) {
          option.hideHelp();
        }
        this._command.addOption(option);
      }
    }
  }

  private _addAction(getExecutor: NonNullable<CloudpackCommandParams<TOptions, TArguments>['getExecutor']>): void {
    const command = this._command;
    // The action function receives parsed options as its first parameter, but we ignore those
    // because they only contain options *specific to this command*, not shared options like `debug`.
    command.action(async () => {
      // Commander has support for custom parsers that parse arguments.
      // Use processedArgs instead of args to get the parsed arguments.
      const processedArgs = command.processedArgs as string[];

      // Get the shared options too, not just the ones for this command.
      // (In `cloudpackCommand`, we added the shared options to each command object so that they show up
      // in help, but commander still seems to respect options only at the first command where they're
      // encountered, which for shared options will always be the program command.)
      const options = command.optsWithGlobals<TOptions & SharedOptions>();

      // Usually `command` will be a sub-command (e.g. "bundle"), but it could also be a sub-sub-command
      // (e.g. "cache clean").
      const verb = command.parent?.parent ? `${command.parent.name()} ${command.name()}` : command.name();

      // Dynamically import the execute function
      const { execute } = await getExecutor();
      if (typeof execute !== 'function') {
        // throw instead of exiting politely, since this is a major developer error
        throw new Error(`No execute function was exported from the command module for ${verb}.`);
      }

      const commandExecutor = new CommandExecutor<TOptions & SharedOptions>({
        args: processedArgs,
        options,
        execute,
        verb,
        ...this._executorOptions,
      });
      await commandExecutor.execute();
    });
  }
}
